import { Type } from '@angular/core';
import { Routes } from '@angular/router';
import { Layout } from './common/layout/layout';
import { StaticLayout } from './static-pages/shell/static-layout';
import { authGuard } from './shared/auth.guard';
import { changePasswordPageGuard } from './shared/change-password-page.guard';
import { mustChangePasswordGuard } from './shared/must-change-password.guard';

/**
 * Every module has two independent versions served side by side:
 *  - dynamic (live, API-integrated) at `/<key>`     → `features/<key>/`
 *  - static  (frozen reference)     at `/static/<key>` → `static-pages/<key>/`
 *
 * Both are generated from this single list so `app.routes.ts` stays maintainable
 * as modules convert. `import()` targets are string literals (not templated), so
 * each route still code-splits into its own lazy chunk.
 *
 * Rule for future work: before starting API integration on a `features/<key>`
 * page, freeze its current markup into `static-pages/<key>/` and it appears here
 * automatically via its `stat` entry. From then on `static-pages/<key>` is frozen.
 */
const MODULES: ReadonlyArray<{
  key: string;
  dyn: () => Promise<Type<unknown>>;
  stat: () => Promise<Type<unknown>>;
}> = [
  { key: 'dashboard', dyn: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard), stat: () => import('./static-pages/dashboard/dashboard').then((m) => m.Dashboard) },
  { key: 'pipeline', dyn: () => import('./features/pipeline/pipeline').then((m) => m.Pipeline), stat: () => import('./static-pages/pipeline/pipeline').then((m) => m.Pipeline) },
  { key: 'clients', dyn: () => import('./features/clients/clients').then((m) => m.Clients), stat: () => import('./static-pages/clients/clients').then((m) => m.Clients) },
  { key: 'appointments', dyn: () => import('./features/appointments/appointments').then((m) => m.Appointments), stat: () => import('./static-pages/appointments/appointments').then((m) => m.Appointments) },
  { key: 'tasks', dyn: () => import('./features/tasks/tasks').then((m) => m.Tasks), stat: () => import('./static-pages/tasks/tasks').then((m) => m.Tasks) },
  { key: 'activities', dyn: () => import('./features/activities/activities').then((m) => m.Activities), stat: () => import('./static-pages/activities/activities').then((m) => m.Activities) },
  { key: 'products', dyn: () => import('./features/products/products').then((m) => m.Products), stat: () => import('./static-pages/products/products').then((m) => m.Products) },
  { key: 'otc-alternatives', dyn: () => import('./features/otc-alternatives/otc-alternatives').then((m) => m.OtcAlternatives), stat: () => import('./static-pages/otc-alternatives/otc-alternatives').then((m) => m.OtcAlternatives) },
  { key: 'indexed-funds', dyn: () => import('./features/indexed-funds/indexed-funds').then((m) => m.IndexedFunds), stat: () => import('./static-pages/indexed-funds/indexed-funds').then((m) => m.IndexedFunds) },
  { key: 'structured', dyn: () => import('./features/structured/structured').then((m) => m.Structured), stat: () => import('./static-pages/structured/structured').then((m) => m.Structured) },
  { key: 'direct-issuances', dyn: () => import('./features/direct-issuances/direct-issuances').then((m) => m.DirectIssuances), stat: () => import('./static-pages/direct-issuances/direct-issuances').then((m) => m.DirectIssuances) },
  { key: 'rwa', dyn: () => import('./features/rwa/rwa').then((m) => m.Rwa), stat: () => import('./static-pages/rwa/rwa').then((m) => m.Rwa) },
  { key: 'wealth-management', dyn: () => import('./features/wealth-management/wealth-management').then((m) => m.WealthManagement), stat: () => import('./static-pages/wealth-management/wealth-management').then((m) => m.WealthManagement) },
  { key: 'trade-finance', dyn: () => import('./features/trade-finance/trade-finance').then((m) => m.TradeFinance), stat: () => import('./static-pages/trade-finance/trade-finance').then((m) => m.TradeFinance) },
  { key: 'escrow', dyn: () => import('./features/escrow/escrow').then((m) => m.Escrow), stat: () => import('./static-pages/escrow/escrow').then((m) => m.Escrow) },
  { key: 'trust', dyn: () => import('./features/trust/trust').then((m) => m.Trust), stat: () => import('./static-pages/trust/trust').then((m) => m.Trust) },
  { key: 'trust-banking', dyn: () => import('./features/trust-banking/trust-banking').then((m) => m.TrustBanking), stat: () => import('./static-pages/trust-banking/trust-banking').then((m) => m.TrustBanking) },
  { key: 'suitability', dyn: () => import('./features/suitability/suitability').then((m) => m.Suitability), stat: () => import('./static-pages/suitability/suitability').then((m) => m.Suitability) },
  { key: 'kyc-aml', dyn: () => import('./features/kyc-aml/kyc-aml').then((m) => m.KycAml), stat: () => import('./static-pages/kyc-aml/kyc-aml').then((m) => m.KycAml) },
  { key: 'documents', dyn: () => import('./features/documents/documents').then((m) => m.Documents), stat: () => import('./static-pages/documents/documents').then((m) => m.Documents) },
  { key: 'compliance', dyn: () => import('./features/compliance/compliance').then((m) => m.Compliance), stat: () => import('./static-pages/compliance/compliance').then((m) => m.Compliance) },
  { key: 'scripts', dyn: () => import('./features/scripts/scripts').then((m) => m.Scripts), stat: () => import('./static-pages/scripts/scripts').then((m) => m.Scripts) },
  { key: 'objections', dyn: () => import('./features/objections/objections').then((m) => m.Objections), stat: () => import('./static-pages/objections/objections').then((m) => m.Objections) },
  { key: 'reporting', dyn: () => import('./features/reporting/reporting').then((m) => m.Reporting), stat: () => import('./static-pages/reporting/reporting').then((m) => m.Reporting) },
  { key: 'management', dyn: () => import('./features/management/management').then((m) => m.Management), stat: () => import('./static-pages/management/management').then((m) => m.Management) },
  { key: 'admin', dyn: () => import('./features/admin/admin').then((m) => m.Admin), stat: () => import('./static-pages/admin/admin').then((m) => m.Admin) },
];

// Dynamic (live) routes — `/<key>`, unchanged behaviour.
const dynamicRoutes: Routes = MODULES.map((m) => ({
  path: m.key,
  loadComponent: m.dyn,
  data: { key: m.key },
}));

// Static (frozen reference) child routes — become `/static/<key>` under the
// StaticLayout shell. Same `key` so the shell's sidebar highlight + breadcrumb
// resolve exactly as for the dynamic route.
const staticChildRoutes: Routes = MODULES.map((m) => ({
  path: m.key,
  loadComponent: m.stat,
  data: { key: m.key, variant: 'static' },
}));

export const routes: Routes = [
  // Live app — dynamic, API-integrated pages under the standard shell.
  {
    path: '',
    component: Layout,
    canActivate: [authGuard],
    canActivateChild: [mustChangePasswordGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

      ...dynamicRoutes,

      // Pipeline create/edit — dynamic-only sub-pages (no static counterpart).
      { path: 'pipeline/new', loadComponent: () => import('./features/pipeline/opportunity-form').then((m) => m.OpportunityForm), data: { key: 'pipeline' } },
      { path: 'pipeline/:id/edit', loadComponent: () => import('./features/pipeline/opportunity-form').then((m) => m.OpportunityForm), data: { key: 'pipeline' } },

      // Appointments create/edit — dynamic-only sub-pages.
      { path: 'appointments/new', loadComponent: () => import('./features/appointments/appointment-form').then((m) => m.AppointmentForm), data: { key: 'appointments' } },
      { path: 'appointments/:id/edit', loadComponent: () => import('./features/appointments/appointment-form').then((m) => m.AppointmentForm), data: { key: 'appointments' } },

      // Follow-up tasks create/edit — dynamic-only sub-pages.
      { path: 'tasks/new', loadComponent: () => import('./features/tasks/task-form').then((m) => m.TaskForm), data: { key: 'tasks' } },
      { path: 'tasks/:id/edit', loadComponent: () => import('./features/tasks/task-form').then((m) => m.TaskForm), data: { key: 'tasks' } },

      // Users management — dynamic-only (RBAC administration).
      { path: 'users', loadComponent: () => import('./features/users/users').then((m) => m.Users), data: { key: 'users' } },
      { path: 'users/new', loadComponent: () => import('./features/users/user-form').then((m) => m.UserForm), data: { key: 'users' } },
      { path: 'users/:id/edit', loadComponent: () => import('./features/users/user-form').then((m) => m.UserForm), data: { key: 'users' } },

      // Roles management — dynamic-only (RBAC administration).
      { path: 'roles', loadComponent: () => import('./features/roles/roles').then((m) => m.Roles), data: { key: 'roles' } },
      { path: 'roles/new', loadComponent: () => import('./features/roles/role-form').then((m) => m.RoleForm), data: { key: 'roles' } },
      { path: 'roles/:id/edit', loadComponent: () => import('./features/roles/role-form').then((m) => m.RoleForm), data: { key: 'roles' } },

      // Account profile — dynamic-only, reached via the topbar user-chip dropdown (no sidebar entry).
      { path: 'profile', loadComponent: () => import('./features/profile/profile').then((m) => m.Profile), data: { key: 'profile' } },

      // All-notifications page — dynamic-only, reached via the topbar bell "View all" (no sidebar entry).
      { path: 'notifications', loadComponent: () => import('./features/notifications/notifications').then((m) => m.Notifications), data: { key: 'notifications' } },
    ],
  },

  // Static reference — frozen, non-API pages under their own StaticLayout shell.
  {
    path: 'static',
    component: StaticLayout,
    children: [
      { path: '', loadComponent: () => import('./static-pages/static-index').then((m) => m.StaticIndex) },
      ...staticChildRoutes,
    ],
  },

  { path: 'login', loadComponent: () => import('./features/login/login').then((m) => m.Login) },
  {
    path: 'change-password',
    loadComponent: () => import('./features/change-password/change-password').then((m) => m.ChangePassword),
    canActivate: [changePasswordPageGuard],
    data: { key: 'change-password' },
  },
  { path: '**', redirectTo: 'dashboard' },
];
